/**
 * Class describing connection between two areas
 *
 * Its descriptor representation kept in area data object is:
 * [{areaId: "area_id_1", index: "0"}{areaId: "area_id_2", index: "3"}]
 *
 * It means that areas with ids "area_id_1" and "area_id_2" are connected on the border indexes 0 and 3
 */

export class Connection {
  constructor(options) {
    this.items = [];
    this.items.push(new Item({ area: options.areaA, index: options.areaIndexA }));
    this.items.push(new Item({ area: options.areaB, index: options.areaIndexB }));
  }

  /**
   * Checks if one of the connected areas is "area" on index "index"
   * @param  {AreaState} area
   * @param  {number} index
   * @returns {boolean}
   */
  hasConnectedAreaOnIndex(area, index) {
    for (const item of this.items) {
      if (item.area.id === area.id && item.index === index) {
        return true;
      }
    }
    return false;
  }

  /**
   * Checks if one of the connected areas is "area"
   * @param  {AreaState} area
   * @returns {boolean}
   */
  hasConnectedArea(areaState) {
    for (const item of this.items) {
      if (item.area.id === areaState.id) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get index on which the provided area is connected
   * @param  {AreaState} areaState
   * @returns {number}
   */
  getIndexOf(areaState) {
    return this.items.filter(item => {
      return item.area.id === areaState.id;
    })[0].index;
  }

  /**
   * Get area state to which is provided area state connected. Return false, if provided area is not part of this connection
   * @param  {AreaState} areaState
   * @returns {AreaState|false}
   */
  getConnectedArea(areaState) {
    if (!this.hasConnectedArea(areaState)) {
      return false;
    }

    return this.items.filter(item => {
      return item.area.id !== areaState.id;
    })[0].area;
  }

  /**
   * Returns JSON representation for backend
   * @returns {Object}
   */
  toJSON() {
    return this.items.map(item => item.toJSON());
  }

  equals(connection) {
    return (
      this.hasConnectedAreaOnIndex(connection.items[0].area, connection.items[0].index) &&
      this.hasConnectedAreaOnIndex(connection.items[1].area, connection.items[1].index)
    );
  }

  getAreas() {
    return this.items.map(item => item.area);
  }
}

class Item {
  constructor(options) {
    ({ area: this.area, index: this.index } = options);
  }

  toJSON() {
    return {
      areaId: this.area.id,
      index: this.index,
    };
  }
}
